/**
 * All-International Requests — the routed, multi-device sibling of International Requests.
 *
 * An agent files a request with one line per device model; FULFILLMENT then decides which route
 * ships how many of each (Amazon MCF, the China factory, the Houston warehouse — one request may
 * use several); only then is it quoted or invoiced, because freight is per SHIPMENT. Tables:
 * migration 065. Design: claude-dev/feature-paullai/2026-10-23-all-international-request/.
 *
 * The pure rules both sides need — the flat freight table and the allocation check — live HERE so
 * the route dialog shows the same number the invoice bills.
 */
import { INTL_DEVICE_MODELS, type IntlDeviceModel } from './intl-requests';

export const INTL_ROUTES = ['MCF', 'CHINA', 'HOUSTON'] as const;
export type IntlRoute = (typeof INTL_ROUTES)[number];

export const INTL_ROUTE_LABELS: Readonly<Record<IntlRoute, string>> = {
  MCF: 'Amazon MCF',
  CHINA: 'China Fulfill',
  HOUSTON: 'Houston Fulfill',
};

/** Routes whose order is placed in SHOPIFY (same SKUs, told apart only by location). */
export const INTL_SHOPIFY_ROUTES: readonly IntlRoute[] = ['CHINA', 'HOUSTON'];

export function isShopifyRoute(route: IntlRoute): boolean {
  return INTL_SHOPIFY_ROUTES.includes(route);
}

// ---- Request number ----------------------------------------------------------------------------

/** Presentation only, as `IR-` — the id is stored bare. A distinct prefix so an AIR and an IR with
 *  the same id can never be mistaken for one record in a chat message. */
export const INTL_ALL_REQUEST_PREFIX = 'AIR';

/** `112` → `AIR-112`. */
export function intlAllRequestNo(id: number | string): string {
  return `${INTL_ALL_REQUEST_PREFIX}-${id}`;
}

/**
 * `AIR-112`, `air 112`, `#112` and `112` all mean row 112. `IR-112` does NOT — that is the other
 * tab's request, and silently reading it as this one would open the wrong customer.
 */
export function parseIntlAllRequestNo(input: string): number | undefined {
  const m = /^\s*(?:AIR[\s-]*)?#?\s*(\d+)\s*$/i.exec(input ?? '');
  if (!m) return undefined;
  const n = Number(m[1]);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

// ---- Flat freight (China / Houston) ------------------------------------------------------------

/** The routes priced from a flat table rather than Amazon's measured fee. */
export type IntlFlatFreightRoute = 'CHINA' | 'HOUSTON';

export interface FlatFreightRates {
  /** What the shipment's FIRST unit costs, by its model. */
  first: Readonly<Record<IntlDeviceModel, number>>;
  /** What every further unit costs, by its model. */
  additional: Readonly<Record<IntlDeviceModel, number>>;
}

/**
 * Flat freight per SHIPMENT, in minor units of the request currency when it has a table
 * (`FLAT_FREIGHT_CURRENCIES`), else USD cents converted on the quote.
 *
 * EUR — the SAME amounts as USD, in euros (Paul, 2026-10-29): OptiStick €50 / €20, Pro Player
 * €60 / €30, ProMax / Kiosk 10 €80 / €40.
 *
 * GBP — its own table (Paul, 2026-10-29): OptiStick £38.50 / £15.40, Pro Player £46.20 / £23.10,
 * ProMax / Kiosk 10 £61.60 / £30.80.
 *
 * Each table is billed as-is, never converted from the USD figure.
 *
 * CHINA — "From China (DDP, customs included)", Paul 2026-10-26: OptiStick $50 first / $20 each
 * additional; Pro Player $60 / $30; ProMax (and Kiosk 10, which this lane does not sell) $80 / $40.
 * OptiKiosk 32 and 50 are NOT covered — those are quoted by Emma.
 *
 * HOUSTON — the SAME table as China (Paul, 2026-10-27: "keep it same as China rate"). It
 * replaced the 2026-10-23 table ($50 first item, +$20 OptiStick, +$30 Pro / ProMax). Only the
 * rates are shared, and since 2026-10-28 so is the VAT treatment: neither route carries our VAT.
 *
 * NOT the MCF rate — MCF freight is Amazon's measured fee (`INTL_FREIGHT_MARKUP`), which replaced
 * an identical-looking 50 + 20 tier on 2026-09-22 because it billed 7-12x Amazon's cost. These
 * tables price shipments Amazon does not carry, so that measurement does not apply to them.
 */
const CHINA_HOUSTON_RATES: FlatFreightRates = {
  first: { OPTISTICK: 5000, PRO: 6000, PROMAX: 8000 },
  additional: { OPTISTICK: 2000, PRO: 3000, PROMAX: 4000 },
};

const CHINA_HOUSTON_RATES_GBP: FlatFreightRates = {
  first: { OPTISTICK: 3850, PRO: 4620, PROMAX: 6160 },
  additional: { OPTISTICK: 1540, PRO: 2310, PROMAX: 3080 },
};

/** The USD table. */
export const FLAT_FREIGHT_RATES: Readonly<Record<IntlFlatFreightRoute, FlatFreightRates>> = {
  CHINA: CHINA_HOUSTON_RATES,
  HOUSTON: CHINA_HOUSTON_RATES,
};

/** Every currency with its own table. Any other currency is the USD figure converted. */
export const FLAT_FREIGHT_TABLES: Readonly<Record<string, Readonly<Record<IntlFlatFreightRoute, FlatFreightRates>>>> = {
  usd: FLAT_FREIGHT_RATES,
  eur: FLAT_FREIGHT_RATES,
  gbp: { CHINA: CHINA_HOUSTON_RATES_GBP, HOUSTON: CHINA_HOUSTON_RATES_GBP },
};

export const FLAT_FREIGHT_CURRENCIES = Object.keys(FLAT_FREIGHT_TABLES);

/** True when `currency` has its own flat table, so the figure needs no FX conversion. */
export function hasFlatFreightTable(currency: string): boolean {
  return FLAT_FREIGHT_CURRENCIES.includes(currency.toLowerCase());
}

/** Units per model in one shipment. Missing or 0 = none of that model. */
export type IntlModelUnits = Partial<Record<IntlDeviceModel, number>>;

/**
 * Flat freight for one China / Houston shipment, in minor units of `currency`, which must have its
 * own table (`hasFlatFreightTable`).
 *
 * MIXED SHIPMENTS: the FIRST item is the shipment's most expensive unit — highest first-item rate,
 * ties broken by the higher additional rate — and every other unit pays its own additional rate.
 * 1 OptiStick + 1 Pro = $60 + $20 = $80; 1 OptiStick + 1 ProMax = $80 + $20 = $100.
 *
 * An empty shipment is 0, not the first-item rate: there is nothing to ship.
 */
export function flatFreightMinor(units: IntlModelUnits, route: IntlFlatFreightRoute, currency = 'usd'): number {
  const table = FLAT_FREIGHT_TABLES[currency.toLowerCase()];
  if (!table) throw new RangeError(`No flat freight table in ${currency.toUpperCase()}.`);
  const rates = table[route];
  let total = 0;
  let firstModel: IntlDeviceModel | undefined;
  for (const model of INTL_DEVICE_MODELS) {
    const qty = units[model] ?? 0;
    if (!Number.isInteger(qty) || qty < 0) {
      throw new RangeError(`Quantity for ${model} must be a whole number >= 0, got ${qty}.`);
    }
    if (qty === 0) continue;
    total += qty * rates.additional[model];
    if (
      firstModel === undefined ||
      rates.first[model] > rates.first[firstModel] ||
      (rates.first[model] === rates.first[firstModel] && rates.additional[model] > rates.additional[firstModel])
    ) {
      firstModel = model;
    }
  }
  if (firstModel === undefined) return 0;
  return total - rates.additional[firstModel] + rates.first[firstModel];
}

// ---- Allocation check --------------------------------------------------------------------------

export interface IntlAllLine {
  deviceModel: IntlDeviceModel;
  quantity: number;
}

export interface IntlAllocation {
  deviceModel: IntlDeviceModel;
  route: IntlRoute;
  quantity: number;
}

/**
 * Every reason a routing cannot be confirmed, as sentences for the dialog; empty = valid.
 *
 * Valid means: each requested model's allocations sum EXACTLY to its line quantity (short is
 * goods nobody ships, over is goods nobody ordered), no allocation names a model that was not
 * requested, and no (model, route) pair appears twice.
 */
export function allocationProblems(
  lines: readonly IntlAllLine[],
  allocations: readonly IntlAllocation[],
): string[] {
  const problems: string[] = [];
  const asked = new Map<IntlDeviceModel, number>();
  for (const l of lines) asked.set(l.deviceModel, l.quantity);

  const seen = new Set<string>();
  const routed = new Map<IntlDeviceModel, number>();
  for (const a of allocations) {
    const key = `${a.deviceModel}/${a.route}`;
    if (seen.has(key)) problems.push(`${a.deviceModel} is allocated to ${a.route} twice.`);
    seen.add(key);
    if (!asked.has(a.deviceModel)) {
      problems.push(`${a.deviceModel} was not requested.`);
      continue;
    }
    if (!Number.isInteger(a.quantity) || a.quantity < 1) {
      problems.push(`${a.deviceModel} → ${a.route}: quantity must be a whole number of at least 1.`);
      continue;
    }
    routed.set(a.deviceModel, (routed.get(a.deviceModel) ?? 0) + a.quantity);
  }
  for (const [model, qty] of asked) {
    const got = routed.get(model) ?? 0;
    if (got !== qty) problems.push(`${model}: ${got} of ${qty} routed.`);
  }
  return problems;
}

/** The units one route ships, from the allocations — the input `flatFreightMinor` takes. */
export function unitsForRoute(allocations: readonly IntlAllocation[], route: IntlRoute): IntlModelUnits {
  const units: IntlModelUnits = {};
  for (const a of allocations) {
    if (a.route !== route) continue;
    units[a.deviceModel] = (units[a.deviceModel] ?? 0) + a.quantity;
  }
  return units;
}

/** The routes the allocations actually use, in `INTL_ROUTES` order. */
export function routesUsed(allocations: readonly IntlAllocation[]): IntlRoute[] {
  return INTL_ROUTES.filter((r) => allocations.some((a) => a.route === r));
}

// ---- Stage --------------------------------------------------------------------------------------

/** DERIVED from the stamps (migration 065 has no status column), latest first in precedence. */
export const INTL_ALL_STAGES = [
  'SHIPPED',
  'IN_FULFILLMENT',
  'PAID',
  'INVOICED',
  'QUOTED',
  'ROUTED',
  'FILED',
] as const;
export type IntlAllStage = (typeof INTL_ALL_STAGES)[number];

export const INTL_ALL_STAGE_LABELS: Readonly<Record<IntlAllStage, string>> = {
  FILED: 'Awaiting routing',
  ROUTED: 'Ready to quote',
  QUOTED: 'Quoted',
  INVOICED: 'Invoiced',
  /** Paid, or approved by root to ship before payment. The key stays PAID: filters and links use it. */
  PAID: 'Ready to fulfil',
  IN_FULFILLMENT: 'In fulfillment',
  SHIPPED: 'Shipped',
};

// ---- Wire shapes (web side) ---------------------------------------------------------------------
//
// What the AIR resolver returns and takes (apps/api/src/app/intl-all-requests/*.response.ts and
// *.inputs.ts). Code-first GraphQL types its strings as `string`; the unions here are what the
// server actually sends. Dates cross the wire as ISO strings.

export interface IntlAllShipmentUnit {
  deviceModel: IntlDeviceModel;
  quantity: number;
}

export interface IntlAllShipment {
  id: number;
  route: IntlRoute;
  routeLabel: string;
  units: IntlAllShipmentUnit[];
  freightMinor?: number | null;
  freightCurrency?: string | null;
  /** AMAZON_PREVIEW | FLAT_TABLE | TYPED */
  freightSource?: string | null;
  /** A freight figure root or fulfilment saved over the system's. */
  freightOverrideMinor?: number | null;
  freightOverrideCurrency?: string | null;
  freightOverrideBy?: string | null;
  freightOverrideAt?: string | null;
  mcfOrderId?: string | null;
  mcfMarketplace?: string | null;
  mcfShippingSpeed?: string | null;
  mcfSubmittedAt?: string | null;
  mcfSubmittedBy?: string | null;
  mcfReleasedAt?: string | null;
  mcfReleasedBy?: string | null;
  mcfCancelledAt?: string | null;
  mcfStatus?: string | null;
  mcfError?: string | null;
  shopifyOrderId?: string | null;
  shopifyOrderName?: string | null;
  /** The order in the admin of the store AIR ships through — built by the api from its config. */
  shopifyAdminUrl?: string | null;
  shopifyLocationId?: string | null;
  /** Set when a create STARTED (the claim) — not proof an order exists. See `isShopifyClaimStuck`. */
  shopifyOrderedAt?: string | null;
  shopifyOrderedBy?: string | null;
  shopifyError?: string | null;
  trackingNumber?: string | null;
  trackingCarrier?: string | null;
  trackingUrl?: string | null;
  trackedAt?: string | null;
  ordered: boolean;
}

export interface IntlAllRequest {
  id: number;
  requestNo: string;
  submittedBy: string;
  submittedAt: string;
  stripeCustomerId: string;
  contactEmail: string;
  contactName?: string | null;
  company?: string | null;
  vatNumber?: string | null;
  accountId?: string | null;
  hubspotContactId?: string | null;
  hubspotDealId?: string | null;
  currency: string;
  poNumber?: string | null;
  shipToName?: string | null;
  shipToLine1?: string | null;
  shipToLine2?: string | null;
  shipToCity?: string | null;
  shipToRegion?: string | null;
  shipToPostalCode?: string | null;
  shipToCountry: string;
  shipToPhone?: string | null;
  addressComplete: boolean;
  addressMissing: string[];
  lastUpdatedBy?: string | null;
  lastUpdatedAt?: string | null;
  lines: IntlAllLine[];
  allocations: IntlAllocation[];
  shipments: IntlAllShipment[];
  routedAt?: string | null;
  routedBy?: string | null;
  stripeQuoteId?: string | null;
  quoteStatus?: string | null;
  quoteNumber?: string | null;
  quoteTotal?: number | null;
  quoteCurrency?: string | null;
  quoteExpiresAt?: string | null;
  quoteExpired: boolean;
  quoteCreatedBy?: string | null;
  quoteCreatedAt?: string | null;
  quoteAcceptedBy?: string | null;
  quoteAcceptedAt?: string | null;
  quoteCanceledAt?: string | null;
  quoteError?: string | null;
  stripeInvoiceId?: string | null;
  invoiceNumber?: string | null;
  invoiceStatus?: string | null;
  invoiceUrl?: string | null;
  invoiceTotal?: number | null;
  invoiceAutoSendAt?: string | null;
  invoicePaidAt?: string | null;
  /** China / Houston orders go to the REAL store — ask before creating one. False on a test store. */
  shopifyLiveStore?: boolean | null;
  /** Root approved shipping before payment: who, when and why. */
  fulfilApprovedAt?: string | null;
  fulfilApprovedBy?: string | null;
  fulfilApprovalReason?: string | null;
  invoiceCheckedAt?: string | null;
  invoiceRaisedBy?: string | null;
  invoiceRaisedAt?: string | null;
  invoiceError?: string | null;
  stage: IntlAllStage;
}

export interface IntlAllRequestPage {
  items: IntlAllRequest[];
  total: number;
}

export interface IntlAllRequestNote {
  id: number;
  staffEmail: string;
  text: string;
  createdAt: string;
}

export interface IntlAllPricedLine {
  deviceModel: IntlDeviceModel;
  quantity: number;
  unitAmount: number;
  amount: number;
  /** Present when the line is one route's share of a model split across a VAT and a no-VAT route. */
  route?: IntlRoute | null;
  routeLabel?: string | null;
  /** Ships from China (DDP, VAT included) or Houston (no VAT) — no VAT is added to these units. */
  vatExempt?: boolean | null;
}

export interface IntlAllPricedShipment {
  route: IntlRoute;
  routeLabel: string;
  freightMinor?: number | null;
  freightSource?: string | null;
  /** Why there is no figure (Amazon could not price it, no FX rate…). Billing refuses until typed. */
  unavailableReason?: string | null;
  costMinor?: number | null;
  speed?: string | null;
  fxNote?: string | null;
  vatExempt?: boolean | null;
  /** The system's figure, when a saved override (source TYPED) replaced it. */
  calculatedMinor?: number | null;
  overriddenBy?: string | null;
  overriddenAt?: string | null;
}

export interface IntlAllPricing {
  currency: string;
  lines: IntlAllPricedLine[];
  shipments: IntlAllPricedShipment[];
  devicesTotal: number;
  freightTotal?: number | null;
  total?: number | null;
  freightBreakdown?: string | null;
  vatPercent?: number | null;
  vatMinor?: number | null;
  totalIncludingVat?: number | null;
}

/** The Products List ticks: which models each kind of route may ship. */
export interface IntlAllEligibleModels {
  mcf: IntlDeviceModel[];
  shopify: IntlDeviceModel[];
}

export interface SubmitIntlAllRequestInput {
  stripeCustomerId: string;
  contactEmail: string;
  contactName?: string;
  company?: string;
  vatNumber?: string;
  accountId?: string;
  hubspotContactId?: string;
  hubspotDealId?: string;
  shipToCountry: string;
  shipToName?: string;
  shipToLine1?: string;
  shipToLine2?: string;
  shipToCity?: string;
  shipToRegion?: string;
  shipToPostalCode?: string;
  shipToPhone?: string;
  lines: IntlAllLine[];
  currency?: string;
}

export interface RouteIntlAllRequestInput {
  requestId: number;
  allocations: IntlAllocation[];
}

export interface IntlAllFreightOverride {
  route: IntlRoute;
  amountMinor: number;
}

export interface CreateIntlAllQuoteInput {
  requestId: number;
  expiresInDays?: number;
  freightOverrides?: IntlAllFreightOverride[];
}

export interface RaiseIntlAllInvoiceInput {
  requestId: number;
  poNumber?: string;
  poDocumentId?: string;
  freightOverrides?: IntlAllFreightOverride[];
}

export interface ConvertIntlAllQuoteInput {
  requestId: number;
  poNumber?: string;
  poDocumentId?: string;
}

/** ROOT + SUPPORT + FULFILLMENT, until routed. The whole address, replacing the old one. */
export interface UpdateIntlAllShippingAddressInput {
  requestId: number;
  shipToName: string;
  shipToLine1: string;
  shipToLine2?: string;
  shipToCity?: string;
  shipToRegion?: string;
  shipToPostalCode?: string;
  shipToCountry: string;
  shipToPhone: string;
}

/** What the list page's Refresh asked Stripe and Shopify / Amazon, and what failed. */
export interface IntlAllRefreshResult {
  invoicesChecked: number;
  shipmentsChecked: number;
  failed: string[];
}

/** ROOT ONLY. Let fulfilment ship an invoiced request before it is paid; the reason is required. */
export interface ApproveIntlAllFulfilmentInput {
  requestId: number;
  reason: string;
}

/** ROOT + FULFILLMENT. `amountMinor` null drops the override; refused once quoted or invoiced. */
export interface SetIntlAllFreightOverrideInput {
  requestId: number;
  route: IntlRoute;
  amountMinor: number | null;
}

export interface ClearIntlAllShopifyClaimInput {
  requestId: number;
  route: IntlRoute;
  /** The order found in Shopify (e.g. `#28860`) to link; absent = clear so Create can be pressed again. */
  shopifyOrderName?: string;
}

export interface IntlAllRequestFilterInput {
  search?: string;
  shipToCountry?: string;
  stage?: IntlAllStage;
  route?: IntlRoute;
  mine?: boolean;
}

// ---- Shipment helpers ---------------------------------------------------------------------------

/**
 * A China / Houston create that STARTED and never recorded an order: the claim is set, there is no
 * order id. The server refuses a second create in this state (it cannot know whether Shopify made
 * the order), so only root's "Clear stuck order" moves it on.
 */
export function isShopifyClaimStuck(s: Pick<IntlAllShipment, 'route' | 'shopifyOrderedAt' | 'shopifyOrderId'>): boolean {
  return isShopifyRoute(s.route) && Boolean(s.shopifyOrderedAt) && !s.shopifyOrderId;
}

// ── Fulfillment inventory (ops sheet + Amazon) ──────────────────────────────────────────────

/** The routes whose stock comes from the ops inventory sheet. MCF stock is read live from Amazon. */
export const INTL_INVENTORY_ROUTES = ['CHINA', 'HOUSTON'] as const;
export type IntlInventoryRoute = (typeof INTL_INVENTORY_ROUTES)[number];

export interface IntlAllInventoryLevel {
  route: IntlInventoryRoute;
  routeLabel: string;
  deviceModel: IntlDeviceModel;
  /** "In stock" on the sheet. Null = not on the sheet, or the sheet could not be read (`error`). */
  onHand: number | null;
  /** Routed here on requests whose order is not created yet. */
  routedNotOrdered: number;
  error: string | null;
}

export interface IntlAllMcfStock {
  marketplace: string;
  marketplaceLabel: string;
  deviceModel: IntlDeviceModel;
  fulfillable: number | null;
  error: string | null;
}
